import {Button, Flag, TableRow} from 'platform/components';
import {Box, HStack, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {type VehicleCampaignResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SalesCampaignStatusFlag} from '@dms/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

const DATE_RANGE_SEPARATOR = '–';

interface SalesCampaignRowProps extends RequiredTestIdProps {
  campaign: VehicleCampaignResponseBody;
  onRemove: () => void;
  isRemoving: boolean;
}

export function SalesCampaignRow(props: SalesCampaignRowProps) {
  const formatDateTime = useDateTimeFormatter();

  const appliedTestId = suffixTestId('applied', props);

  const validity = [props.campaign.validFrom, props.campaign.validTo]
    .map((date) => formatDateTime('dateShort', parseDate(date)))
    .join(` ${DATE_RANGE_SEPARATOR} `);

  const membershipAppearance = match(props.campaign.membershipType)
    .with('MANUAL', () => ({
      label: i18n.t('entity.salesCampaign.membershipType.manual'),
      dotColor: 'palettes.blue.60.100' as const,
    }))
    .with('FILTER', () => ({
      label: i18n.t('entity.salesCampaign.membershipType.filter'),
      dotColor: 'palettes.teal.60.100' as const,
    }))
    .with('ALL_VEHICLES', () => ({
      label: i18n.t('entity.salesCampaign.membershipType.allVehicles'),
      dotColor: 'palettes.purple.60.100' as const,
    }))
    .exhaustive();

  const isEnded = props.campaign.status === 'ENDED';
  const hasStatusDot = props.campaign.status === 'ACTIVE';
  const rowOpacity = isEnded ? 0.5 : undefined;

  return (
    <TableRow data-testid={props['data-testid']}>
      <Box opacity={rowOpacity}>
        <Text size="small" alternative data-testid={suffixTestId('name', props)}>
          {props.campaign.name}
        </Text>
      </Box>

      <Box opacity={rowOpacity}>
        <HStack spacing={1} align="center" data-testid={suffixTestId('membershipType', props)}>
          <Box
            width={2}
            height={2}
            borderRadius="circular"
            backgroundColor={membershipAppearance.dotColor}
          />
          <Text size="small" color="secondary">
            {membershipAppearance.label}
          </Text>
        </HStack>
      </Box>

      <Box opacity={rowOpacity}>
        <Text size="small" color="secondary" data-testid={suffixTestId('validity', props)}>
          {validity}
        </Text>
      </Box>

      <Box opacity={rowOpacity}>
        <HStack spacing={1} align="center">
          <Show when={!hasStatusDot}>
            <Box width={2} />
          </Show>
          <SalesCampaignStatusFlag
            status={props.campaign.status}
            data-testid={suffixTestId('status', props)}
          />
        </HStack>
      </Box>

      <Box opacity={rowOpacity}>
        {match(props.campaign)
          .with({status: 'ACTIVE', isApplied: true}, () => (
            <Flag
              label={i18n.t('entity.salesCampaign.labels.applied')}
              colorScheme="yellow"
              size="default"
              data-testid={appliedTestId}
            />
          ))
          .with({status: 'ACTIVE'}, () => (
            <Text size="small" color="secondary" data-testid={appliedTestId}>
              {i18n.t('entity.salesCampaign.labels.lowerPriority')}
            </Text>
          ))
          .otherwise(() => (
            <Text size="small" color="secondary" data-testid={appliedTestId}>
              {EMPTY_PLACEHOLDER}
            </Text>
          ))}
      </Box>

      <Box opacity={rowOpacity}>
        <HStack justify="flex-end">
          {isEnded ? (
            <Button
              variant="link"
              title={i18n.t('general.actions.detail')}
              to={composePath(settingsRoutes.campaignEdit, {params: {id: props.campaign.id}})}
              data-testid={suffixTestId('detail', props)}
            />
          ) : (
            <Button
              variant="dangerLink"
              title={i18n.t('entity.salesCampaign.actions.removeVehicleFromCampaign')}
              onClick={props.onRemove}
              isLoading={props.isRemoving}
              isDisabled={props.isRemoving}
              data-testid={suffixTestId('remove', props)}
            />
          )}
        </HStack>
      </Box>
    </TableRow>
  );
}
